import { format } from 'date-fns';
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Package,
  ReceiptEuro,
  type LucideIcon,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { Invoice } from '@octave/contracts';

/** A round icon action with a tooltip, matching the invoice row style. */
export function IconAction({
  icon: Icon,
  label,
  onClick,
  danger,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors',
              danger
                ? 'hover:bg-red-50 hover:text-red-500'
                : 'hover:bg-slate-100 hover:text-slate-600'
            )}
          >
            <Icon className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function TypeBadge({ invoice }: { invoice: Invoice }) {
  if (invoice.kind === 'DETAIL') {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
        Détail
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold',
        invoice.type === 'EQUIPMENT'
          ? 'bg-sky-50 text-sky-700'
          : 'bg-emerald-50 text-emerald-700'
      )}
    >
      {invoice.type === 'EQUIPMENT' ? 'Équipement' : 'Forfait'}
    </span>
  );
}

function rowIcon(invoice: Invoice): LucideIcon {
  if (invoice.kind === 'DETAIL') return ReceiptEuro;
  return invoice.type === 'EQUIPMENT' ? Package : FileText;
}

/** One invoice (or nested billing detail) row. `children` renders below it. */
export function InvoiceRow({
  invoice,
  actions,
  compact,
  meta,
  children,
}: {
  invoice: Invoice;
  actions?: ReactNode;
  compact?: boolean;
  /** Extra inline info next to the badge (e.g. "lié à …"). */
  meta?: ReactNode;
  children?: ReactNode;
}) {
  const Icon = rowIcon(invoice);
  return (
    <div className="space-y-1.5">
      <div
        className={cn(
          'group flex items-center gap-4 rounded-xl border transition-all duration-200',
          compact
            ? 'border-slate-200 bg-slate-50 px-4 py-2.5'
            : 'border-transparent bg-white px-5 py-4 hover:scale-[1.005] hover:border-emerald-100 hover:shadow-md active:scale-[0.995]'
        )}
      >
        <div
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full bg-slate-100 transition-colors group-hover:bg-emerald-50',
            compact ? 'h-8 w-8' : 'h-10 w-10'
          )}
        >
          <Icon className="h-4 w-4 text-slate-400 group-hover:text-emerald-600" />
        </div>

        <div className="grid min-w-0 flex-1 grid-cols-1 items-center gap-x-6 sm:grid-cols-[1fr_auto_auto]">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900">
              {invoice.reference}
            </p>
            <div className="mt-0.5 flex items-center gap-2">
              <TypeBadge invoice={invoice} />
              {meta}
              {invoice.sites.length > 0 && (
                <span className="text-xs text-slate-400">
                  {invoice.sites.length} site
                  {invoice.sites.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          <span className="hidden text-sm text-slate-400 sm:block">
            {format(new Date(invoice.date), 'dd/MM/yyyy')}
          </span>

          <div className="hidden items-center gap-1 sm:flex">{actions}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

/** Collapsible list of the billing details attached to an invoice. */
export function LinkedDetails({
  details,
  renderActions,
}: {
  details: Invoice[];
  renderActions: (detail: Invoice) => ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div className="ml-5 mt-1.5 space-y-1.5 border-l-2 border-slate-200 pl-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
        {details.length} détail{details.length > 1 ? 's' : ''} lié
        {details.length > 1 ? 's' : ''}
      </button>
      {open &&
        details.map((detail) => (
          <InvoiceRow
            key={detail.id}
            invoice={detail}
            compact
            actions={renderActions(detail)}
          />
        ))}
    </div>
  );
}
